import React, { useContext, useEffect, useId, useRef, useState } from "react";
import {
	Grow,
	ListItemIcon,
	ListItemText,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	SvgIcon,
	Typography,
	Tooltip,
} from "@mui/material";
import { SupportModal, ThemeWrapper, useMobileLayout, useOverflow } from "@verity/components";
import {
	CoreIconButton,
	CoreLabelTextContent,
	CoreMenuItem,
	CoreMenuPopover,
	CoreProfileImage,
	VerityLogoWithCircle,
} from "@verity/components/core";
import apiClient from "../../helpers/api-client";
import { openAuthenticatedTab } from "../../helpers/open-authenticated";
import i18n from "../../i18n";
import { PortalControllerContext } from "../context";
import { getLogoutUrl, useIsAdmin, useIsInstructor } from "../shared";
import { PortalMenuButton } from "./portal-menu";
import SelectCourseModal from "./select-course-modal";
import styles from "./portal-top-bar.module.css";
import SupportIcon from "../../images/portal-navigation/support.svg?react";
import Switch from "../../images/portal-navigation/switch.svg?react";
import PopoutIcon from "../../images/portal-navigation/popoutIcon.svg?react";
import SettingsIcon from "../../images/portal-navigation/settings.svg?react";
import SetupIcon from "../../images/portal-navigation/userSetup.svg?react";
import ExtensionIcon from "../../images/portal-navigation/extensionProfile.svg?react";
import LockdownIcon from "../../images/portal-navigation/lockdownProfile.svg?react";
import MobileIcon from "../../images/portal-navigation/mobileIconProfile.svg?react";
import ListNumbersIcon from "../../images/portal-navigation/listNumbers.svg?react";
import SignOutIcon from "../../images/portal-navigation/signOut.svg?react";

function SetupSubmenu({ setMenuShowing }: { setMenuShowing: (open: boolean) => void }) {
	const { pages, navigateToPage } = useContext(PortalControllerContext);
	const [hoveringButton, setHoveringButton] = useState(false);
	const [hoveringPopper, setHoveringPopper] = useState(false);
	const [focusingPopper, setFocusingPopper] = useState(false);
	const userIsInstructor = useIsInstructor();

	const extensionIsAllowed = "extensionSetup" in pages;
	const lockdownIsAllowed = "lockdownSetup" in pages;
	const mobileIsAllowed = "mobileSetup" in pages;

	const setupRef = useRef<HTMLLIElement>(null);
	const popperRef = useRef<HTMLUListElement>(null);

	const submenuOpen = hoveringButton || hoveringPopper || focusingPopper;

	const handleSetupKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowLeft" || e.key === "Enter") {
			e.preventDefault();
			setFocusingPopper(true);
			setTimeout(() => {
				const firstSubItem = popperRef.current?.querySelector("li");
				if (firstSubItem) (firstSubItem as HTMLElement).focus();
			}, 0);
		}
	};

	const handleSubMenuKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			e.preventDefault();
			e.stopPropagation();

			setTimeout(() => {
				const items = Array.from(popperRef.current?.querySelectorAll('li[role="menuitem"]') ?? []);

				const currentIndex = items.findIndex((element) => element === document.activeElement);
				let nextFocusIndex = currentIndex;

				if (e.key === "ArrowUp" && items.length > 0) {
					nextFocusIndex = currentIndex !== 0 ? (currentIndex - 1 + items.length) % items.length : items.length - 1;
				}

				if (e.key === "ArrowDown" && items.length > 0) {
					nextFocusIndex = currentIndex !== items.length - 1 ? (currentIndex + 1) % items.length : 0;
				}

				if (items[nextFocusIndex]) {
					(items[nextFocusIndex] as HTMLElement).focus();
				}
			}, 0);

			return;
		}

		if (e.key === "ArrowRight" || e.key === "Escape") {
			e.preventDefault();
			e.stopPropagation();
			setFocusingPopper(false);
			setupRef.current?.focus();
		}
	};

	return (
		<>
			{!userIsInstructor && (extensionIsAllowed || lockdownIsAllowed || mobileIsAllowed) && (
				<>
					<MenuItem
						data-testid="setup-tab"
						ref={setupRef}
						onKeyDown={handleSetupKeyDown}
						onMouseEnter={() => setHoveringButton(true)}
						onMouseLeave={() => setHoveringButton(false)}
					>
						<ListItemIcon className={styles.iconSpacing}>
							<SvgIcon>
								<SetupIcon />
							</SvgIcon>
						</ListItemIcon>
						<ListItemText>{i18n.t("portal.components.portalTopBarTSX.setup")}</ListItemText>
					</MenuItem>
					<Popper
						open={submenuOpen}
						anchorEl={setupRef.current}
						placement="left"
						disablePortal
						transition
						onMouseEnter={() => setHoveringPopper(true)}
						onMouseLeave={() => setHoveringPopper(false)}
						onFocus={() => setFocusingPopper(true)}
						onBlur={() => setFocusingPopper(false)}
						modifiers={[{ name: "offset", options: { offset: [0, 1] } }]}
					>
						{({ TransitionProps }) => (
							<Grow {...TransitionProps} style={{ transformOrigin: "right center" }}>
								<Paper elevation={3} style={{ width: "100%", height: "100%" }}>
									<MenuList
										ref={popperRef}
										onKeyDownCapture={handleSubMenuKeyDown}
										variant="selectedMenu"
										disableListWrap={false}
										role="menu"
									>
										{extensionIsAllowed && (
											<MenuItem
												data-testid="extension-setup-tab"
												onClick={() => {
													navigateToPage("extensionSetup");
													setMenuShowing(false);
												}}
												role="menuitem"
											>
												<ListItemIcon className={styles.iconSpacing}>
													<SvgIcon>
														<ExtensionIcon />
													</SvgIcon>
												</ListItemIcon>
												<ListItemText>{i18n.t("portal.components.portalTopBarTSX.browserExtension")}</ListItemText>
											</MenuItem>
										)}
										{lockdownIsAllowed && (
											<MenuItem
												data-testid="lockdown-setup-tab"
												onClick={() => {
													navigateToPage("lockdownSetup");
													setMenuShowing(false);
												}}
												role="menuitem"
											>
												<ListItemIcon className={styles.iconSpacing}>
													<SvgIcon>
														<LockdownIcon />
													</SvgIcon>
												</ListItemIcon>
												<ListItemText>{i18n.t("portal.components.portalTopBarTSX.lockdownApp")}</ListItemText>
											</MenuItem>
										)}
										{mobileIsAllowed && (
											<MenuItem
												data-testid="mobile-setup-tab"
												onClick={() => {
													navigateToPage("mobileSetup");
													setMenuShowing(false);
												}}
												role="menuitem"
											>
												<ListItemIcon className={styles.iconSpacing}>
													<SvgIcon>
														<MobileIcon />
													</SvgIcon>
												</ListItemIcon>
												<ListItemText>{i18n.t("portal.components.portalTopBarTSX.mobileApp")}</ListItemText>
											</MenuItem>
										)}
									</MenuList>
								</Paper>
							</Grow>
						)}
					</Popper>
				</>
			)}
		</>
	);
}

function UserProfileImage({ size, alt }: { size?: number; alt?: string }) {
	const userInfo = useContext(PortalControllerContext)?.userInfo?.userInfo;
	return (
		<CoreProfileImage
			className={styles.profileIcon}
			size={size}
			name={userInfo?.full_name}
			alt={alt}
			role="presentation"
		/>
	);
}

function UserMenuButton() {
	const { topFrame, portalContext, openQuickSetupGuide, userInfo, navigateToPage } =
		useContext(PortalControllerContext);
	const { lms } = portalContext;
	const userName = userInfo?.userInfo?.full_name;
	const userEmail = userInfo?.userInfo?.email;
	const userIsAdmin = useIsAdmin();
	const userIsInstructor = useIsInstructor();
	const isMobileLayout = useMobileLayout();

	const buttonRef = useRef<HTMLButtonElement>(null);
	const [menuShowing, setMenuShowing] = useState(false);
	const [supportModalShowing, setSupportModalShowing] = useState(false);
	const [callSupportAccessCode, setCallSupportAccessCode] = useState<number | undefined>(undefined);

	const buttonId = useId();
	const popoverId = useId();
	const closeMenu = () => setMenuShowing(false);

	useEffect(() => {
		const fetchAccessCode = async () => {
			if (!userIsInstructor) return;
			try {
				const response = await apiClient.support.getCallAccessCode();
				setCallSupportAccessCode(response?.accessCode ?? undefined);
			} catch (error) {
				console.error(error);
			}
		};

		fetchAccessCode();
	}, [userIsInstructor]);

	return (
		<>
			<Tooltip title={i18n.t("portal.components.portalTopBarTSX.myAccount")}>
				<CoreIconButton
					id={buttonId}
					variant="plain"
					className={styles.profileMenuButton}
					onClick={() => setMenuShowing(true)}
					aria-haspopup
					aria-expanded={menuShowing}
					aria-controls={popoverId}
					ref={buttonRef}
				>
					<UserProfileImage alt="" />
				</CoreIconButton>
			</Tooltip>
			{menuShowing && (
				<CoreMenuPopover id={popoverId} open onClose={closeMenu} anchorEl={buttonRef.current}>
					<div className={styles.userMenu}>
						<div className={styles.menuInfoArea}>
							<UserProfileImage size={44} alt="" />
							<div className={styles.menuInfoAreaDetails}>
								{userName && (
									<Tooltip title={userName}>
										<div className={styles.userName}>{userName}</div>
									</Tooltip>
								)}
								{userEmail && (
									<Tooltip title={userEmail}>
										<div className={styles.infoAreaText}>{userEmail}</div>
									</Tooltip>
								)}
							</div>
						</div>
						<MenuList autoFocusItem={menuShowing} aria-labelledby={buttonId}>
							{userIsAdmin && (lms === "canvas" || lms === "desire2learn" || lms === "moodle") && (
								<CoreMenuItem
									data-testid="guide-tab"
									onClick={() => {
										openQuickSetupGuide(true);
										closeMenu();
									}}
								>
									<ListItemIcon className={styles.iconSpacing}>
										<SvgIcon>
											<ListNumbersIcon />
										</SvgIcon>
									</ListItemIcon>
									<ListItemText>{i18n.t("portal.components.portalTopBarTSX.quickSetup")}</ListItemText>
								</CoreMenuItem>
							)}
							<CoreMenuItem
								data-testid="settings-tab"
								onClick={() => {
									navigateToPage("userSettings");
									closeMenu();
								}}
							>
								<ListItemIcon className={styles.iconSpacing}>
									<SvgIcon>
										<SettingsIcon />
									</SvgIcon>
								</ListItemIcon>
								<ListItemText>{i18n.t("portal.components.portalTopBarTSX.settings")}</ListItemText>
							</CoreMenuItem>
							<CoreMenuItem
								data-testid="support-tab"
								onClick={() => {
									setSupportModalShowing(true);
									closeMenu();
								}}
							>
								<ListItemIcon className={styles.iconSpacing}>
									<SvgIcon>
										<SupportIcon />
									</SvgIcon>
								</ListItemIcon>
								<ListItemText>{i18n.t("portal.components.portalTopBarTSX.support")}</ListItemText>
							</CoreMenuItem>
							<SetupSubmenu setMenuShowing={setMenuShowing} />
							{topFrame && (
								<CoreMenuItem
									data-testid="logout"
									onClick={async () => {
										await apiClient.setAccountId(undefined);
										await apiClient.setRefreshToken(undefined);
										await apiClient.setAccessToken(undefined);
										await apiClient.authentication.logout();
										setMenuShowing(false);
										window.location.href = getLogoutUrl(portalContext.homeOrigin);
									}}
								>
									<ListItemIcon className={styles.iconSpacing}>
										<SvgIcon>
											<SignOutIcon />
										</SvgIcon>
									</ListItemIcon>
									<ListItemText>{i18n.t("portal.components.portalTopBarTSX.signOut")}</ListItemText>
								</CoreMenuItem>
							)}
						</MenuList>
					</div>
				</CoreMenuPopover>
			)}
			<SupportModal
				iframeSrc={window.location.origin + "/support-modal/index.html"}
				open={supportModalShowing}
				setOpen={setSupportModalShowing}
				showCallSupport={userIsInstructor}
				callSupportAccessCode={userIsInstructor ? callSupportAccessCode : undefined}
				isMobileLayout={isMobileLayout}
			/>
		</>
	);
}

export default function PortalTopBar() {
	const { returnToAssessmentInfo, topFrame, courseInfo, changePortalContext, currPage, canSwitchCourse } =
		useContext(PortalControllerContext);
	const isMobileLayout = useMobileLayout();

	const [courseSelectShowing, setCourseSelectShowing] = useState(false);

	useEffect(() => {
		setCourseSelectShowing(false);
	}, [currPage]);

	const [courseNameRef, courseNameOverflowing] = useOverflow();

	const courseName = <span>{courseInfo?.name}</span>;

	const courseArea = (
		<div className={styles.courseArea}>
			<div className={styles.courseText}>
				<CoreLabelTextContent header={i18n.t("portal.components.portalTopBarTSX.course")} />
				<span className={styles.courseName} ref={courseNameRef}>
					{courseNameOverflowing && courseInfo?.name ? (
						<Tooltip title={courseInfo.name}>{courseName}</Tooltip>
					) : (
						courseName
					)}
				</span>
			</div>
			<CoreIconButton
				variant="plain"
				size="large"
				color="#515151"
				onClick={() => setCourseSelectShowing(true)}
				tooltip={i18n.t("portal.components.portalTopBarTSX.switchCourse")}
				data-testid="switch-course"
			>
				<SvgIcon>
					<Switch />
				</SvgIcon>
			</CoreIconButton>
		</div>
	);

	return (
		<ThemeWrapper
			scopedCssBaselineProps={{
				className: `${styles.topBar} ${isMobileLayout && canSwitchCourse ? styles.stacked : ""}`,
			}}
		>
			<header style={{ display: "contents" }}>
				<div className={styles.mainRow}>
					{isMobileLayout && <PortalMenuButton expandedTopBar={canSwitchCourse && !courseSelectShowing} />}
					<div className={styles.logoArea}>
						<VerityLogoWithCircle
							width={40}
							applyBorder
							alt={i18n.t("portal.components.portalTopBarTSX.verityLogoAriaLabel")}
						/>
						<Typography component="span" className={styles.title}>
							{i18n.t("portal.components.portalTopBarTSX.verity")}
						</Typography>
					</div>
					{!returnToAssessmentInfo && (
						<div className={styles.rightArea}>
							{canSwitchCourse && !isMobileLayout && courseArea}
							<div className={styles.actionArea}>
								{!topFrame && (
									<CoreIconButton
										variant="plain"
										size="large"
										onClick={() => openAuthenticatedTab(new URL(window.location.href))?.focus()}
										tooltip={i18n.t("portal.components.portalTopBarTSX.openNewTab")}
										data-testid="new-tab"
									>
										<SvgIcon>
											<PopoutIcon />
										</SvgIcon>
									</CoreIconButton>
								)}
								<UserMenuButton />
							</div>
						</div>
					)}
				</div>
				{canSwitchCourse && isMobileLayout && !courseSelectShowing && courseArea}
			</header>
			{courseSelectShowing && (
				<SelectCourseModal
					onClose={() => setCourseSelectShowing(false)}
					onSelect={(courseId) =>
						changePortalContext({
							courseId,
							assessmentId: null,
						})
					}
					description={i18n.t("portal.components.portalTopBarTSX.switchCourseDescription")}
				/>
			)}
		</ThemeWrapper>
	);
}
